import { useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, Building2, Mail, Lock, User, Phone, Hash, Check, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useRegister } from '@/hooks/useAuth';
import { passwordChecks, passwordProblem } from '@/lib/password-rules';
import { isValidPhoneNumber } from '@/lib/pk-mobile';
import { slugFromName } from '@/lib/register-form';

type RegisterFormData = {
  tenantName: string;
  tenantSlug: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone?: string;
  acceptTerms: boolean;
};

export function RegisterPage() {
  const { t } = useTranslation('auth');
  // POS-I234: the trial length comes from the server setting (DEFAULT_TRIAL_DAYS), never a typed 30
  const { data: publicCatalog } = useQuery<{ trialDays?: number }>({
    queryKey: ['public-subscription-catalog'],
    queryFn: () => api.get('/subscriptions/public/catalog').then((r) => r.data),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const registerSchema = useMemo(
    () =>
      z
        .object({
          tenantName: z.string().trim()
            .min(2, t('businessNameLength'))
            .max(100, t('businessNameLength'))
            .regex(/^[^<>]*$/, t('businessNameNoBrackets')),
          tenantSlug: z.string()
            .min(3, t('businessIdLength'))
            .max(40, t('businessIdLength'))
            .regex(/^[a-z0-9-]+$/, t('businessIdFormat')),
          firstName: z.string().trim().min(1, t('firstNameRequired')).max(50, t('firstNameLength')),
          lastName: z.string().trim().min(1, t('lastNameRequired')).max(50, t('lastNameLength')),
          email: z.string().email(t('invalidEmail')).max(254, t('emailTooLong')),
          password: z.string().superRefine((value, ctx) => {
            const problem = passwordProblem(value);
            if (problem) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t(problem === 'common' ? 'passwordCommon' : 'passwordRules') });
            }
          }),
          confirmPassword: z.string(),
          phone: z.string().optional().refine((value) => isValidPhoneNumber(value), t('phoneInvalid')),
          // POS-I185: required on this page (the API keeps it optional so older clients still work)
          acceptTerms: z.boolean().refine((v) => v === true, t('termsRequired')),
        })
        .superRefine((data, ctx) => {
          if (data.confirmPassword !== data.password) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: t('passwordsDontMatch') });
          }
          if (passwordProblem(data.password, data.email) === 'common') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: t('passwordCommon') });
          }
        }),
    [t],
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // A second click in the same tick runs before React has disabled the button (POS-I180)
  const submitting = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    setError,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // A refusal from the server that belongs to one field is shown under that field
  const registerMutation = useRegister((field, message) => setError(field, { type: 'server', message }));

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    // Revalidate like the registered fields do once a submit has been tried
    setValue('tenantName', name, { shouldValidate: isSubmitted });
    setValue('tenantSlug', slugFromName(name), { shouldValidate: isSubmitted });
  };

  const password = watch('password') ?? '';
  const checks = passwordChecks(password, watch('email'));

  const onSubmit = (data: RegisterFormData) => {
    if (submitting.current) return;
    submitting.current = true;
    // confirmPassword is checked in the form and never sent
    registerMutation.mutate(
      {
        tenantName: data.tenantName.trim(),
        tenantSlug: data.tenantSlug,
        email: data.email,
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone: data.phone?.trim() || undefined,
        acceptTerms: data.acceptTerms === true,
      },
      { onSettled: () => { submitting.current = false; } },
    );
  };

  return (
    <Card className="border-0 shadow-xl glass-card animate-scale-in">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold animate-fade-in-up">{t('createYourAccount')}</CardTitle>
        <CardDescription>{t('registerDescription')}</CardDescription>
        {publicCatalog?.trialDays ? (
          <p className="text-sm text-muted-foreground">{t('registerTrialNote', { count: publicCatalog.trialDays })}</p>
        ) : null}
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tenantName" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('businessName')}
            </Label>
            <div className="relative">
              <Building2 className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="tenantName"
                placeholder={t('businessNamePlaceholder')}
                maxLength={100}
                className="ps-9 h-11"
                {...register('tenantName')}
                onChange={handleNameChange}
              />
            </div>
            {errors.tenantName && (
              <p className="text-xs text-destructive-text">{errors.tenantName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenantSlug" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('businessId')}
            </Label>
            <div className="relative">
              <Hash className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="tenantSlug"
                placeholder="my-store"
                maxLength={40}
                className="ps-9 h-11"
                {...register('tenantSlug')}
              />
            </div>
            {errors.tenantSlug && (
              <p className="text-xs text-destructive-text">{errors.tenantSlug.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('firstName')}
              </Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="firstName" placeholder={t('firstNamePlaceholder')} maxLength={50} className="ps-9 h-11" {...register('firstName')} />
              </div>
              {errors.firstName && (
                <p className="text-xs text-destructive-text">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('lastName')}
              </Label>
              <div className="relative">
                <User className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="lastName" placeholder={t('lastNamePlaceholder')} maxLength={50} className="ps-9 h-11" {...register('lastName')} />
              </div>
              {errors.lastName && (
                <p className="text-xs text-destructive-text">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('email')}
            </Label>
            <div className="relative">
              <Mail className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="admin@mystore.com"
                maxLength={254}
                className="ps-9 h-11"
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-destructive-text">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('phoneOptional')}
            </Label>
            <div className="relative">
              <Phone className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="phone"
                dir="ltr"
                type="tel"
                inputMode="tel"
                maxLength={20}
                placeholder="+923001234567"
                className="ps-9 h-11"
                {...register('phone')}
              />
            </div>
            {errors.phone ? (
              <p className="text-xs text-destructive-text">{errors.phone.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t('phoneHelper')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('password')}
            </Label>
            <div className="relative">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                maxLength={72}
                autoComplete="new-password"
                className="ps-9 pe-10 h-11"
                {...register('password')}
              />
              <button
                type="button"
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-destructive-text">{errors.password.message}</p>
            )}
            <ul className="space-y-0.5 text-xs" aria-live="polite">
              {[
                { ok: checks.basics, label: t('passwordHintBasics') },
                { ok: checks.notCommon, label: t('passwordHintCommon') },
              ].map(({ ok, label }) => (
                <li
                  key={label}
                  className={ok ? 'flex items-center gap-1.5 text-success-text' : 'flex items-center gap-1.5 text-muted-foreground'}
                >
                  {ok ? <Check className="h-3 w-3 shrink-0" /> : <Circle className="h-3 w-3 shrink-0" />}
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('confirmPassword')}
            </Label>
            <div className="relative">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                placeholder="••••••••"
                maxLength={72}
                autoComplete="new-password"
                className="ps-9 pe-10 h-11"
                {...register('confirmPassword')}
              />
              <button
                type="button"
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirm ? t('hidePassword') : t('showPassword')}
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-xs text-destructive-text">{errors.confirmPassword.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex items-start gap-2">
              <Checkbox id="acceptTerms" className="mt-0.5" {...register('acceptTerms')} />
              <Label htmlFor="acceptTerms" className="text-sm font-normal leading-snug">
                <Trans
                  i18nKey="termsAgree"
                  ns="auth"
                  components={{
                    terms: <a href="https://gridx.app/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-text underline" />,
                    privacy: <a href="https://gridx.app/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-text underline" />,
                  }}
                />
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-xs text-destructive-text">{errors.acceptTerms.message}</p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 pt-2">
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-semibold shadow-glow transition-all"
            disabled={registerMutation.isPending}
          >
            {registerMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t('registerButton')}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/login" className="font-medium text-primary-text hover:text-primary-text/80">
              {t('signIn')}
            </Link>
          </p>
          <div className="flex justify-center pt-1">
            <LanguageSwitcher size="sm" />
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
